import React, { useState, useEffect, useRef } from 'react';
import './ThinkingIndicator.css';
import AvatarGif from './AvatarGif';

const thinkingMessages = [
  "Channeling my inner Sherlock....",
  "Putting the last brick on the wall...",
  "Almost finished — no need for popcorn yet...",
  "Tuning the compass — I know the direction...",
  "Translating silence into insight...",
  "Listening between the words...",
  "Mental gymnastics in progress...",
  "I’d love to know right away too...",
  "Thinking carefully, so you don’t have to...",
  "Just flipping the mental pancakes...",
  "Elegance in progress...",
  "Running low-latency brilliance.exe",
  "Folding this answer into shape — origami style..."
];

const MemoizedAvatar = React.memo(() => (
  <AvatarGif
    height={16}
    width={16}
    alt="Assistant Avatar"
    data-testid="thinking-avatar-gif"
  />
));

const ThinkingIndicator: React.FC = () => {
  const initialRandomIndex = Math.floor(Math.random() * thinkingMessages.length);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(initialRandomIndex);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [message, setMessage] = useState(thinkingMessages[initialRandomIndex]);
  const messageRef = useRef<HTMLDivElement>(null);
  const timeoutsRef = useRef<number[]>([]);

  const clearAllTimeouts = () => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  };

  const addTimeout = (callback: () => void, delay: number): number => {
    const id = window.setTimeout(callback, delay);
    timeoutsRef.current.push(id);
    return id;
  };

  const getNextMessageIndex = (): number => {
    return (currentMessageIndex + 1) % thinkingMessages.length;
  };

  useEffect(() => {
    const fadeOutDuration = 250; // 0.25s for fade out
    const visibleDuration = 5000; // 5s for text display

    const runAnimationCycle = () => {
      addTimeout(() => {
        setIsFadingOut(true);
        addTimeout(() => {
          const nextIndex = getNextMessageIndex();

          setCurrentMessageIndex(nextIndex);
          setMessage(thinkingMessages[nextIndex]);
          addTimeout(() => {
            setIsFadingOut(false);
            addTimeout(runAnimationCycle, visibleDuration);
          }, 50); // Small delay to ensure the new text is ready before fading in
        }, fadeOutDuration);
      }, visibleDuration);
    };

    clearAllTimeouts();
    runAnimationCycle();
    return clearAllTimeouts;
  }, []); // Empty dependency array - only run once on mount

  return (
    <div className="thinking-container" data-testid="thinking-container">
      <div className="thinking-avatar" data-testid="thinking-avatar-container">
        <MemoizedAvatar />
      </div>
      <div className="thinking-message-container" data-testid="thinking-message-container">
        <div 
          ref={messageRef}
          className={`thinking-message ${isFadingOut ? 'fade-out' : ''}`}
          data-testid="thinking-message"
        >
          {message}
        </div>
      </div>
    </div>
  );
};

export default ThinkingIndicator;
